// Player-facing names, glyph codes, hotkeys and fictional sponsors for every content key (GDD §9, §13, §14). Each
// table is a full Record over its key union, so a new content key without presentation is a compile error.
// Sponsors are invented megacorps: never a real company (GDD §13 tone rules).

import type { AiTier, BuildingKey, DeployDoctrine, LaunchDoctrine, StrikeKey, StrikeTargeting, TaxSetting, UnitDef, UnitKey } from '@taxwar/content'

export interface ItemPresentation {
  readonly name: string
  /** Three-character glyph code shown on bar tiles and menus. */
  readonly code: string
  readonly sponsor: string
}

/** A hotkey by physical key (KeyboardEvent.code), so the bar layout survives non-QWERTY keyboards. */
export interface Hotkey {
  readonly code: string
  readonly label: string
}

export const UNIT_PRESENTATION: Readonly<Record<UnitKey, ItemPresentation>> = {
  conscripts: { name: 'Conscripts', code: 'CNS', sponsor: 'Patriot Staffing Solutions' },
  tank: { name: 'Tank', code: 'TNK', sponsor: 'OmniDefense™' },
  artillery: { name: 'Artillery', code: 'ART', sponsor: 'Boomwell Munitions' },
  mobile_sam: { name: 'Mobile SAM', code: 'SAM', sponsor: 'CloudCeiling Subscriptions™' },
  fighter: { name: 'Fighter Jet', code: 'FTR', sponsor: 'Freedom Dynamics' },
  bomber: { name: 'Bomber', code: 'BMB', sponsor: 'Freedom Dynamics' },
  drone_swarm: { name: 'Drone Swarm', code: 'DRN', sponsor: 'Lowest Bidder Aerospace' },
  destroyer: { name: 'Destroyer', code: 'DST', sponsor: 'MegaHull Maritime™' },
  carrier: { name: 'Carrier', code: 'CVN', sponsor: 'MegaHull Maritime™' },
}

export const BUILDING_PRESENTATION: Readonly<Record<BuildingKey, ItemPresentation>> = {
  tax_office: { name: 'Tax Office', code: 'TAX', sponsor: 'Revenue Maximization Partners' },
  airbase: { name: 'Airbase', code: 'AIR', sponsor: 'SkyTab Airframes' },
  port: { name: 'Port', code: 'PRT', sponsor: 'MegaHull Maritime™' },
  sam_site: { name: 'SAM Site', code: 'SAM', sponsor: 'CloudCeiling Subscriptions™' },
  fortress: { name: 'Fortress', code: 'FRT', sponsor: 'Bunkr Luxury Shelters' },
  propaganda_office: { name: 'Ministry of Truth', code: 'MoT', sponsor: 'CrowdPulse Media Group' },
}

export const STRIKE_PRESENTATION: Readonly<Record<StrikeKey, ItemPresentation>> = {
  precision_strike: { name: 'Precision Strike', code: 'PRC', sponsor: 'Boomwell Munitions' },
  shock_and_awe: { name: 'Shock & Awe', code: 'S&A', sponsor: 'Boomwell Munitions' },
  sanctions: { name: 'Sanctions', code: 'SNC', sponsor: 'SanctionSoft Compliance Cloud' },
  leaflet_drop: { name: 'Leaflet Drop', code: 'LFT', sponsor: 'Leafletron Print Solutions' },
}

/** A doctrine as the HUD shows it (GDD §7.1): a short tile tag and the rule in words. */
export interface DoctrineCopy {
  readonly tag: string
  readonly rule: string
}

export const DEPLOY_DOCTRINE_COPY: Readonly<Record<DeployDoctrine, DoctrineCopy>> = {
  frontline: { tag: 'FRONTLINE', rule: 'Deploys at your city nearest the enemy capital' },
  richest: { tag: 'RICHEST', rule: 'Deploys at your highest-revenue city' },
  rearmost: { tag: 'REARMOST', rule: 'Deploys at your city farthest from the enemy capital' },
  capital: { tag: 'CAPITAL', rule: 'Deploys at your capital, or the city nearest it' },
  loudest: { tag: 'LOUDEST', rule: 'Deploys at your most damaged city' },
  alphabetical: { tag: 'A–Z', rule: 'Deploys at your city whose name sorts first' },
  lowest_bidder: { tag: 'RANDOM', rule: 'Deploys at a random city' },
}

export const STRIKE_TARGETING_COPY: Readonly<Record<StrikeTargeting, DoctrineCopy>> = {
  priciest: { tag: 'PRICIEST', rule: 'Hits the most expensive enemy unit or building in sight' },
  crowd: { tag: 'CROWD', rule: 'Hits the biggest enemy crowd in sight' },
  closest_to_home: { tag: 'CLOSEST', rule: 'Hits the enemy unit nearest your capital' },
}

export const LAUNCH_DOCTRINE_COPY: Readonly<Record<LaunchDoctrine, string>> = {
  nearest: 'launches from your city nearest the target',
  farthest: 'launches from your city farthest from the target',
}

/** A unit's deploy-site requirement as its Arsenal tile shows it (GDD §7), keyed like SiteState. */
export interface RequirementCopy {
  /** Badge while one of your standing cities can launch the unit. */
  readonly met: string
  /** Badge while none can. */
  readonly unmet: string
  readonly rule: string
  /** The requirement is a building: its met badge is a check you earned (land units only need a city). */
  readonly needsBuilding: boolean
}

export const DEPLOY_REQUIREMENT_COPY: Readonly<Record<UnitDef['deployRequires'], RequirementCopy>> = {
  none: { met: 'ANY CITY', unmet: '✗ NO CITY', rule: 'Deploys from any of your cities', needsBuilding: false },
  airbase_or_capital: { met: '✓ AIRBASE/CAP', unmet: '✗ NO AIRBASE', rule: 'Needs your capital or a city with an Airbase', needsBuilding: true },
  port: { met: '✓ PORT', unmet: '✗ NO PORT', rule: 'Needs a Port, which only coastal cities can build (click the city)', needsBuilding: true },
}

/** Unaimed strikes (Sanctions) invoice the enemy as a whole. */
export const NATIONWIDE_STRIKE_COPY: DoctrineCopy = { tag: 'NATIONWIDE', rule: 'Hits the enemy nation as a whole' }

/** The deadpan reason each purchase lands where it does (GDD §7.1); shown in tile tooltips and deploy headlines. */
export const UNIT_QUIPS: Readonly<Record<UnitKey, string>> = {
  conscripts: 'The recruitment brochure said "travel".',
  tank: 'Deployed where the parade route is.',
  artillery: 'Safety first. Range second.',
  mobile_sam: 'The minister lives there.',
  fighter: 'Scrambled to whichever city complained loudest.',
  bomber: 'Filed under A. Paperwork is paperwork.',
  drone_swarm: "Launch site chosen by the lowest bidder's algorithm.",
  destroyer: "The admiral's memoir needs a chapter.",
  carrier: 'Nobody asked where the fighters go.',
}

export const STRIKE_QUIPS: Readonly<Record<StrikeKey, string>> = {
  precision_strike: 'Aimed at the most expensive thing in sight. Accounting insisted.',
  shock_and_awe: 'The contractor bills by the mile.',
  sanctions: 'Nothing personal. Everything invoiced.',
  leaflet_drop: "Dropped on whoever is closest to the minister's house.",
}

/** GDD §14: hotkeys 1–9 for the Arsenal. */
export const UNIT_HOTKEYS: Readonly<Record<UnitKey, Hotkey>> = {
  conscripts: { code: 'Digit1', label: '1' },
  tank: { code: 'Digit2', label: '2' },
  artillery: { code: 'Digit3', label: '3' },
  mobile_sam: { code: 'Digit4', label: '4' },
  fighter: { code: 'Digit5', label: '5' },
  bomber: { code: 'Digit6', label: '6' },
  drone_swarm: { code: 'Digit7', label: '7' },
  destroyer: { code: 'Digit8', label: '8' },
  carrier: { code: 'Digit9', label: '9' },
}

/** GDD §14: Q/W/E/R for the Strike bar. */
export const STRIKE_HOTKEYS: Readonly<Record<StrikeKey, Hotkey>> = {
  precision_strike: { code: 'KeyQ', label: 'Q' },
  shock_and_awe: { code: 'KeyW', label: 'W' },
  sanctions: { code: 'KeyE', label: 'E' },
  leaflet_drop: { code: 'KeyR', label: 'R' },
}

export const TAX_LABELS: Readonly<Record<TaxSetting, string>> = {
  relief: 'Relief',
  low: 'Low',
  standard: 'Standard',
  high: 'High',
  wartime: 'Wartime',
}

export interface AiTierPresentation {
  readonly name: string
  readonly blurb: string
}

export const AI_TIER_PRESENTATION: Readonly<Record<AiTier, AiTierPresentation>> = {
  recruit: { name: 'Recruit', blurb: 'Reads the manual during the war.' },
  veteran: { name: 'Veteran', blurb: 'Has seen things. Mostly invoices.' },
  general: { name: 'General', blurb: 'Knows what the tax dial is for.' },
  warlord: { name: 'Warlord', blurb: 'Holds shares in both sides.' },
}

/** Ticker ads between headlines (ART-DIRECTION §1 corporate-dystopia layer). */
export const SPONSOR_SLOGANS: readonly string[] = [
  'OmniDefense™ — Because peace is a market inefficiency.',
  'Freedom Dynamics: liberty, now in convenient monthly payments.',
  'Lowest Bidder Aerospace — Close enough.™',
  'MegaHull Maritime™: we float. Usually.',
  'CrowdPulse Media Group — your opinion, pre-installed.',
  'Leafletron Print Solutions: paper beats rock. Ask anyone.',
  'SanctionSoft Compliance Cloud — now with 40% more embargo.',
  'Boomwell Munitions: going out with a bang since last quarter.',
  'Bunkr Luxury Shelters — the view is terrible, the lease is worse.',
]
